import { BarChartData, BarChartInfo, Info } from "./BarChartProps";

export const assignYAxisIdToInfo = (
  info: BarChartInfo,
  orientation: "ltr" | "rtl"
): Map<string, Info> => {
  const [firstKey, secondKey] = Array.from(info.keys());
  const newInfo = new Map<string, Info>();

  newInfo.set(firstKey, {
    ...(info.get(firstKey) as Info),
    yAxisId: orientation === "ltr" ? "left" : "right",
  });

  newInfo.set(secondKey, {
    ...(info.get(secondKey) as Info),
    yAxisId: orientation === "ltr" ? "right" : "left",
  });

  return newInfo;
};

export type PortfolioDistributionChartProp = {
  label: string;
  proportion: number;
  quarter: string;
};

/**
 * @param rawData
 * @returns {BarChartData[]} result
 * @example rawData
 * [
 *   { label: "Real Estate", proportion: 0.03, quarter: "2020-03-31" },
 *   { label: "Financials", proportion: 0.04, quarter: "2020-03-31" },
 *   { label: "Real Estate", proportion: 0.04, quarter: "2020-06-31" },
 *   { label: "Financials", proportion: 0.03, quarter: "2020-06-31" },
 *   { label: "Others", proportion: 0.05, quarter: "2020-09-31" },
 * ]
 * @example result
 * [
 *   { name: '2020-03-31', "Real Estate": 0.03, "Financials": 0.04 },
 *   { name: '2020-06-31', "Real Estate": 0.04, "Financials": 0.03, "Others": 0.05 },
 * ]
 */

export const getPortfolioDistributionChartData = ({
  rawData,
}: {
  rawData: PortfolioDistributionChartProp[];
}) => {
  const result: BarChartData[] = [];
  for (const { quarter, proportion, label } of rawData) {
    let quarterInfo = result.find((item) => item.name === quarter);
    if (!quarterInfo) {
      quarterInfo = { name: quarter };
      result.push(quarterInfo);
    }
    // to make sure sum of proportion is 100, we need to multiply 100
    if (label) {
      quarterInfo[label] = Math.round(proportion * 100);
    }
  }
  return result;
};

/**
 *
 * 利用 colors 來產生 info，如果 color 的 key 為定義好的 label，則直接使用該 label。
 * 如果 colors 的 key 不是定義好的 label（number），則使用 dataKeys 裡面的 key 作為 label。
 *
 * @input
 * const data = [
 *   { label: "A", ... },
 *   { label: "B", ... },
 *   { label: "A", ... },
 *   { label: "C", ... },
 * ];
 *
 * const colors = new Map<string, string>([
 *    [0, "#58C1D7CC"],
 *    [1, "#2D80AFCC"],
 *    [2, "#003D70CC"],
 *    ["Others", "#EA4335CC"],
 * ])
 *
 * @derivatives
 * const dataKeys = ["A", "B", "C"];
 *
 * @returns
 * info = new Map<string, string>([
 *  ["A", { barColor: "#58C1D7CC", stackId: "stackId" }],
 *  ["B", { barColor: "#2D80AFCC", stackId: "stackId" }],
 *  ["C", { barColor: "#003D70CC", stackId: "stackId" }],
 *  ["Others", { barColor: "#EA4335CC", stackId: "stackId" }],
 * ])
 */
export const getPortfolioDistributionChartInfo = ({
  data,
  colors,
}: {
  data: PortfolioDistributionChartProp[];
  colors: Map<string | number, string>;
}) => {
  const dataKeys = Array.from(new Set(data.map(({ label }) => label)));

  let index = 0;
  return new Map(
    dataKeys.map((key) => {
      const color = colors.get(key) || colors.get(index++) || "#000";
      return [key, { barColor: color, stackId: "stackId" }];
    })
  );
};
